import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { fetchTooltipData } from '@actions/tooltips-actions';
import ActivityLogTooltip from '@components/activity-logs/tooltips';
import { colors } from '@constants/colors';
import { capitalize } from 'lodash';
import {
  getActivityLogTooltipsConfig,
  getEntityTypeLabel
} from '@constants/config';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import TriptychWidget from '@shared/list-item/widgets/triptych-widget';
import { getAgencyIcon } from '@utils/icon-utils';
import './tooltip.scss';

const EntityTooltip = ({ children, dataType, id, text }) => {
  const dispatch = useDispatch();
  const data = useSelector(state => state.tooltips.map[id]);
  const error = useSelector(state => state.tooltips.error.map?.[id]);

  useEffect(() => {
    if (!data && !error && id && dataType) {
      dispatch(fetchTooltipData('map', dataType, id));
    }
  }, [dataType, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFetch = useCallback(() => {
    dispatch(fetchTooltipData('map', dataType, id));
  }, [dispatch, dataType, id]);

  const handleLinkClick = useCallback(() => {
    window.open(`/map?${dataType}=${id}`, '_blank');
  }, [dataType, id]);

  const getFromCategoryDict = useCallback((fieldName, valueField) => {
    if (data?.category_dict) {
      for (let i = 0; i < data.category_dict.length; i += 1) { // eslint-disable-line id-length
        if (data.category_dict[i].name === fieldName) {
          return data.category_dict[i][valueField];
        }
      }
    }
    return null;
  }, [data]);

  const getRow = useCallback(rowConfig => {
    if (!data) {
      return null;
    }
    if (rowConfig.type === 'field') {
      return data[rowConfig['field-name']];
    }
    if (rowConfig.type === 'category') {
      return getFromCategoryDict(rowConfig['category-name'], 'value');
    }
    if (rowConfig.type === 'triptych') {
      const status = getFromCategoryDict(rowConfig['status-name'], 'value');
      const priority = getFromCategoryDict('priority', 'id');
      return (
        <TriptychWidget
          name={status}
          priority={priority}
          start={data.start_date}
          end={data.end_date}
        />
      );
    }
    return null;
  }, [data, getFromCategoryDict]);

  const renderHeader = useCallback(() => {
    if (!data) {
      return null;
    }
    const tooltipsConfig = getActivityLogTooltipsConfig();
    const { field, label } = R.pathOr({}, ['header', dataType], tooltipsConfig);
    return (
      <div styleName="body-header body-item can-overflow">
        <div styleName="id">
          ID: {data.id}
        </div>
        {field && label && data[field] && (
          <div styleName="row can-overflow">
            <div styleName="bull">&bull;</div>
            <div>
              {label}: {data[field]}
            </div>
          </div>
        )}
      </div>
    );
  }, [data, dataType]);

  const renderTitle = useCallback(() => data?.name, [data]);

  const renderInlineLabel = useCallback(() => {
    if (data?.name) {
      return (
        <span styleName="entity-inline-label">
          {capitalize(getEntityTypeLabel(dataType))} {data.name}
          {' '}(ID {id})
        </span>
      );
    }
    return children || text;
  }, [children, data, dataType, id, text]);

  const renderContent = useCallback(() => {
    if (!data) {
      return null;
    }
    const tooltipsConfig = getActivityLogTooltipsConfig();
    const { row1, row2 } = R.pathOr({}, ['body', dataType], tooltipsConfig);
    return (
      <div styleName="body-content">
        <div styleName="body-row body-item">
          {row1 && getRow(row1)}
        </div>
        <div styleName="body-row">
          {row2 && getRow(row2)}
        </div>
        {((data.owner_list && data.owner_list.length > 0) || data.agency) && (
          <div styleName="sep" />
        )}
        {data.owner_list && data.owner_list.length > 0 && (
          <div styleName="body-row body-item body-user-icon-row">
            <div styleName="icon">
              <div styleName="avatar">
                <AccountCircleIcon htmlColor={colors.coordinate.blue} style={{ fontSize: '1.125rem' }} />
              </div>
            </div>
            {data.owner_list.join(', ')}
          </div>
        )}
        {data.agency && (
          <div styleName="body-row body-item body-agency-icon-row">
            <img
              alt=""
              src={getAgencyIcon({ id: data.agency_type })}
              styleName="icon"
            />
            {data.agency_name}
          </div>
        )}
      </div>
    );
  }, [data, dataType, getRow]);

  return (
    <ActivityLogTooltip
      data={data}
      error={error}
      onFetch={handleFetch}
      renderHeader={renderHeader}
      renderTitle={renderTitle}
      renderContent={renderContent}
      linkLabel={`VIEW ${getEntityTypeLabel(dataType)}`}
      onLinkClick={handleLinkClick}
      dataType={dataType}
    >
      {renderInlineLabel()}
    </ActivityLogTooltip>
  );
};

EntityTooltip.propTypes = {
  children: PropTypes.node,
  dataType: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  text: PropTypes.string
};

export default EntityTooltip;
